/**
 * CardScanner.jsx — v213.2
 *
 * v213.2 (C-A-190, C-A-191, C-A-192):
 *  - Bigger camera frame: 92% width / 420px max (was 85% / 340px)
 *  - Per-field OCR confidence (returned by API) drives an edit-and-confirm
 *    step for ID cards before the photo is "used". DOB and Sex are always
 *    confirmable; low-confidence fields are highlighted.
 *  - Brand colors throughout: NAVY #15354F / BLUE #46A8F9 / GREEN #66D170.
 *    Removed off-brand purple #6366f1 and non-brand green #22c55e.
 */
import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { RotateCcw, Check, X, Camera, Sparkles, AlertTriangle, Upload } from 'lucide-react'
import { aiAPI } from '../services/api'
import { useI18n } from '../i18n'
import { createPortal } from 'react-dom'
import DobPicker from './DobPicker'
// TEST 2.25 (P5 step 16): the confirm step only ever shows for an ID front, which is the
// account holder's own card, so its DOB is judged 18+ like every other account-holder DOB.
import { ageVerdict, ACCOUNT_HOLDER_MIN_AGE } from '../utils/age'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

// Brand colors
const BRAND_NAVY = '#15354F'
const BRAND_BLUE = '#46A8F9'
const BRAND_LIGHT_BLUE = '#A7D9FC'
const BRAND_ICE = '#E9F8FF'
const BRAND_GREEN = '#66D170'

export default function CardScanner({
  onCapture,
  onCancel,
  onUpload,          // v240.11.0: pick a file from the device instead of the live camera
  cardType = 'ID',
  side = 'front',
  initialImage = null,
  enableOCR = true,  // Enable OCR extraction by default
  patientState = '', // PROD-1190: the patient's address state, sent with an insurance card's OCR
}) {
  const { t } = useI18n()
  const [mode, setMode] = useState(initialImage ? 'preview' : 'camera')
  const [capturedImage, setCapturedImage] = useState(initialImage)
  const [processing, setProcessing] = useState(false)
  const [extracting, setExtracting] = useState(false)
  const [extractedData, setExtractedData] = useState(null)
  // TEST 1.02 R2 — the server's answer on whether a scanned ID front will be KEPT.
  const [idNameCheck, setIdNameCheck] = useState(null)
  const [confidence, setConfidence] = useState({})
  const [confirmMode, setConfirmMode] = useState(false)
  const [editedDob, setEditedDob] = useState('')
  const [editedSex, setEditedSex] = useState('')
  // v213.8: "Also detected" fields are now editable so the patient can
  // correct mismatched OCR (especially address — common when they've moved
  // since their license was issued).
  const [editedFirstName, setEditedFirstName] = useState('')
  const [editedLastName, setEditedLastName] = useState('')
  const [editedStreetAddress, setEditedStreetAddress] = useState('')
  const [editedCity, setEditedCity] = useState('')
  const [editedState, setEditedState] = useState('')
  const [editedZipCode, setEditedZipCode] = useState('')
  const [cameraError, setCameraError] = useState(null)
  const [cameraReady, setCameraReady] = useState(false)

  const videoRef = useRef(null)
  const streamRef = useRef(null)
  // v224.5.4: refs to crop to the visible guide frame (not the whole scene)
  const stageRef = useRef(null)
  const frameRef = useRef(null)
  // v240.11.0: hidden input for the in-scanner "upload from device" path
  const uploadInputRef = useRef(null)

  const CARD_ASPECT_RATIO = 1.586
  const OUTPUT_WIDTH = 1000
  const OUTPUT_HEIGHT = Math.round(OUTPUT_WIDTH / CARD_ASPECT_RATIO)

  // v213.2 (C-A-191/192): Whether this card needs the patient confirm step.
  // Only ID front-side cards trigger the DOB/Sex confirm UI.
  const needsConfirm = useMemo(
    () => cardType === 'ID' && side === 'front' && extractedData &&
          (extractedData.dob !== undefined || extractedData.sex !== undefined),
    [cardType, side, extractedData]
  )

  // v228.8.6: which sides we OCR — front always, plus insurance backs (for the
  // card-printed payer ID). Mirrors the capture-time gate; used to pick the
  // right "captured" vs "auto-detected" preview chrome.
  const ocrSide = enableOCR && (side === 'front' || (cardType !== 'ID' && side === 'back'))

  useEffect(() => {
    if (mode === 'camera') {
      startCamera()
    }
    return () => stopCamera()
  }, [mode])

  const startCamera = async () => {
    try {
      setCameraError(null)
      setCameraReady(false)

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      })

      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play()
            .then(() => {
              console.log('Camera ready, has size:', !!videoRef.current.videoWidth)
              setCameraReady(true)
            })
            .catch(err => {
              console.error('Video play error:', err)
              setCameraError(t('errors2.couldNotStartCamera'))
            })
        }
      }
    } catch (err) {
      console.error('Camera error:', err)
      setCameraError(t('errors2.cameraAccessDenied'))
    }
  }

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
  }

  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !cameraReady) {
      alert(t('errors2.cameraNotReady'))
      return
    }

    const video = videoRef.current
    const vw = video.videoWidth
    const vh = video.videoHeight

    if (vw === 0 || vh === 0) {
      alert(t('errors2.cameraNotReady'))
      return
    }

    setProcessing(true)

    try {
      // v224.5.4: crop to the on-screen guide frame (mapped through the video's
      // object-fit:cover scaling) so only what the patient frames is captured —
      // previously this cropped a full-height center slice and brought the whole
      // scene with it. Falls back to the old center-card-aspect crop if the frame
      // can't be measured.
      let sx, sy, sw, sh
      const stageEl = stageRef.current
      const frameEl = frameRef.current
      if (stageEl && frameEl) {
        const sRect = stageEl.getBoundingClientRect()
        const fRect = frameEl.getBoundingClientRect()
        const cw = sRect.width, ch = sRect.height
        const scale = Math.max(cw / vw, ch / vh) // object-fit: cover
        const offsetX = (cw - vw * scale) / 2
        const offsetY = (ch - vh * scale) / 2
        sx = ((fRect.left - sRect.left) - offsetX) / scale
        sy = ((fRect.top - sRect.top) - offsetY) / scale
        sw = fRect.width / scale
        sh = fRect.height / scale
        sx = Math.max(0, Math.min(sx, vw)); sy = Math.max(0, Math.min(sy, vh))
        sw = Math.max(1, Math.min(sw, vw - sx)); sh = Math.max(1, Math.min(sh, vh - sy))
      } else {
        const targetRatio = CARD_ASPECT_RATIO
        const videoRatio = vw / vh
        if (videoRatio > targetRatio) {
          sh = vh; sw = vh * targetRatio; sx = (vw - sw) / 2; sy = 0
        } else {
          sw = vw; sh = vw / targetRatio; sx = 0; sy = (vh - sh) / 2
        }
      }

      const canvas = document.createElement('canvas')
      canvas.width = OUTPUT_WIDTH
      canvas.height = OUTPUT_HEIGHT
      const ctx = canvas.getContext('2d')
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT)

      const result = canvas.toDataURL('image/jpeg', 0.9)
      console.log('Captured:', (result.length * 0.75 / 1024).toFixed(1), 'KB')

      stopCamera()
      setCapturedImage(result)
      setMode('preview')

      // v228.8.6: OCR the insurance BACK too — the EDI/claims payer ID
      // (cardPayerId) the server resolver prefers is printed on the back, not
      // the front. ID backs stay capture-only (no PHI we need from them).
      const shouldOcrThisSide = enableOCR && (side === 'front' || (cardType !== 'ID' && side === 'back'))
      if (shouldOcrThisSide) {
        setExtracting(true)
        try {
          const ocrResult = await aiAPI.extractCardData(result, cardType, side, { patientState })
          // PROD-903: flags only — the OCR answer is the card's name, date of birth and member id.
          console.log('[CardScanner] OCR result:', { success: !!ocrResult?.success, extracted: !!ocrResult?.extracted })
          setIdNameCheck((ocrResult && typeof ocrResult.idNameCheck === 'object') ? ocrResult.idNameCheck : null)
          if (ocrResult.success && ocrResult.extracted) {
            setExtractedData(ocrResult.extracted)
            setConfidence(ocrResult.confidence || {})
            if (cardType === 'ID') {
              setEditedDob(ocrResult.extracted.dob || '')
              setEditedSex(ocrResult.extracted.sex || '')
              // v213.8: seed editable Also-Detected fields from OCR
              setEditedFirstName(ocrResult.extracted.firstName || '')
              setEditedLastName(ocrResult.extracted.lastName || '')
              setEditedStreetAddress(ocrResult.extracted.streetAddress || '')
              setEditedCity(ocrResult.extracted.city || '')
              setEditedState(ocrResult.extracted.state || '')
              setEditedZipCode(ocrResult.extracted.zipCode || '')
            }
          }
        } catch (err) {
          console.error('[CardScanner] OCR error:', err)
        }
        setExtracting(false)
      }
    } catch (err) {
      console.error('Capture error:', err)
      alert('Failed to capture')
    }

    setProcessing(false)
  }, [cameraReady, OUTPUT_WIDTH, OUTPUT_HEIGHT, CARD_ASPECT_RATIO, enableOCR, cardType, side, patientState])

  // v240.11.0: in-scanner upload. The live camera is full-screen, so the slot's
  // "upload" affordance is hidden behind it — previously the only way to switch to
  // a file was to X out manually (confusing). This lets the patient pick a file
  // from inside the scanner; we hand it to the parent's existing upload pipeline
  // (same OCR + storage as the slot) and the scanner auto-closes (parent clears
  // its open-scanner state). No-op if the host didn't wire onUpload.
  const handleUploadPick = () => { uploadInputRef.current?.click() }
  const handleUploadFile = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    stopCamera()
    onUpload?.(file)
  }

  const handleRetake = () => {
    setCapturedImage(null)
    setExtractedData(null)
    setIdNameCheck(null)
    setConfidence({})
    setConfirmMode(false)
    setEditedDob('')
    setEditedSex('')
    setEditedFirstName('')
    setEditedLastName('')
    setEditedStreetAddress('')
    setEditedCity('')
    setEditedState('')
    setEditedZipCode('')
    setMode('camera')
  }

  const handleConfirmPhoto = () => {
    if (needsConfirm) {
      setConfirmMode(true)
    } else {
      finalizeCapture(extractedData)
    }
  }

  const handleSubmitConfirmed = () => {
    const merged = { ...(extractedData || {}) }
    if (cardType === 'ID') {
      if (editedDob && editedDob.trim()) {
        merged.dob = editedDob.trim()
      } else {
        delete merged.dob
      }
      if (editedSex) {
        merged.sex = editedSex
      } else {
        delete merged.sex
      }
      // v213.8: also write edited "Also detected" fields back so the
      // patient's corrections (especially address) propagate.
      if (editedFirstName.trim()) merged.firstName = editedFirstName.trim()
      if (editedLastName.trim()) merged.lastName = editedLastName.trim()
      if (editedStreetAddress.trim()) merged.streetAddress = editedStreetAddress.trim()
      if (editedCity.trim()) merged.city = editedCity.trim()
      if (editedState.trim()) merged.state = editedState.trim()
      if (editedZipCode.trim()) merged.zipCode = editedZipCode.trim()
      merged._patientConfirmed = { dob: true, sex: true }
    }
    finalizeCapture(merged)
  }

  const finalizeCapture = (dataToReturn) => {
    onCapture({
      data: capturedImage,
      url: capturedImage,
      name: `${cardType.toLowerCase()}_${side}_${Date.now()}.jpg`,
      extractedData: dataToReturn,
      idNameCheck
    })
  }

  const formatFieldName = (key) => {
    const names = {
      firstName: 'First Name',
      lastName: 'Last Name',
      dob: 'Date of Birth',
      sex: 'Sex',
      streetAddress: 'Address',
      city: 'City',
      state: 'State',
      zipCode: 'ZIP',
      provider: 'Insurance',
      memberId: 'Member ID',
      groupNumber: 'Group #',
      subscriberName: 'Subscriber',
      planName: 'Plan',
      rxBin: 'RX BIN',
      rxPcn: 'RX PCN'
    }
    return names[key] || key
  }

  // ===== Render: Confirm step (ID front only) =====
  if (confirmMode && capturedImage) {
    const dobConfLow = confidence.dob && confidence.dob !== 'high'
    const sexConfLow = confidence.sex && confidence.sex !== 'high'
    const dobVerdict = ageVerdict(editedDob, ACCOUNT_HOLDER_MIN_AGE)
    const dobValid = !!(editedDob && /^\d{4}-\d{2}-\d{2}$/.test(editedDob)) && dobVerdict === 'ok'
    const sexValid = editedSex === 'male' || editedSex === 'female' || editedSex === 'other'

    return createPortal((
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: '#000', zIndex: 99999,
        display: 'flex', flexDirection: 'column'
      }}>
        <div style={{
          padding: '16px',
          paddingTop: 'max(16px, env(safe-area-inset-top))',
          backgroundColor: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <button
            onClick={() => setConfirmMode(false)}
            style={{
              background: 'transparent', border: 'none', color: 'white',
              fontSize: '14px', cursor: 'pointer', padding: '8px',
              fontFamily: 'Inter, sans-serif'
            }}
          >
            ← Back
          </button>
          <div style={{ color: 'white', textAlign: 'center' }}>
            <div style={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>{t('cardScanner.confirmYourInfo', 'Confirm your info')}</div>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>{t('cardScanner.doubleCheckDetails', 'Please double-check these details')}</div>
          </div>
          <div style={{ width: 40 }} />
        </div>

        <div style={{
          flex: 1, overflowY: 'auto', padding: '20px',
          display: 'flex', flexDirection: 'column', gap: '16px',
          maxWidth: '480px', width: '100%', margin: '0 auto'
        }}>
          <img
            src={capturedImage}
            alt="Captured ID"
            style={{
              width: '100%', borderRadius: 12,
              boxShadow: '0 8px 28px rgba(0,0,0,0.5)'
            }}
          />

          {(dobConfLow || sexConfLow) && (
            <div style={{
              backgroundColor: BRAND_ICE,
              border: `1px solid ${BRAND_LIGHT_BLUE}`,
              borderRadius: 12, padding: '12px 14px',
              display: 'flex', gap: 10, alignItems: 'flex-start'
            }}>
              <AlertTriangle size={18} color={BRAND_NAVY} style={{ flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontSize: 13, color: BRAND_NAVY, lineHeight: 1.4, fontFamily: 'Inter, sans-serif' }}>
                {t('cardScanner.notSureFields')}
              </div>
            </div>
          )}

          <div>
            <label style={{
              display: 'block', fontSize: 13, fontWeight: 600,
              color: 'white', marginBottom: 6, fontFamily: 'Inter, sans-serif'
            }}>
              {t('identification.dateOfBirth', 'Date of Birth')}
              {dobConfLow && (
                <span style={{
                  marginLeft: 8, fontSize: 11, fontWeight: 500,
                  color: BRAND_NAVY, backgroundColor: '#FFE9A7',
                  padding: '2px 8px', borderRadius: 999
                }}>{t('cardScanner.pleaseVerify', 'Please verify')}</span>
              )}
            </label>
            {/* TF-01 SWEEP (v242.63.0): the DOB read off an ID card is still a
                DOB, and this one was found by grepping EVERY type="date" in the
                app rather than only the screen the report named. DobPicker renders
                its own field, so the low-confidence amber ring moves to a wrapper. */}
            <div style={{
              borderRadius: 10,
              boxShadow: dobConfLow ? '0 0 0 2px #F59E0B' : 'none',
            }}>
              <DobPicker value={editedDob} onChange={setEditedDob} minAge={18} />
            </div>
            {dobVerdict === 'minor' && (
              <p role="alert" style={{ fontSize: 12.5, color: '#FCA5A5', marginTop: 6, fontFamily: 'Inter, sans-serif' }}>
                {t('dob.tooYoung', { defaultValue: 'You must be {{age}} or older to use Orella.', age: ACCOUNT_HOLDER_MIN_AGE })}
              </p>
            )}
          </div>

          <div>
            <label style={{
              display: 'block', fontSize: 13, fontWeight: 600,
              color: 'white', marginBottom: 6, fontFamily: 'Inter, sans-serif'
            }}>
              {t('identification.sex', 'Sex')}
              {sexConfLow && (
                <span style={{
                  marginLeft: 8, fontSize: 11, fontWeight: 500,
                  color: BRAND_NAVY, backgroundColor: '#FFE9A7',
                  padding: '2px 8px', borderRadius: 999
                }}>{t('cardScanner.pleaseVerify', 'Please verify')}</span>
              )}
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {['male', 'female', 'other'].map(opt => {
                const sel = editedSex === opt
                return (
                  <button
                    key={opt}
                    onClick={() => setEditedSex(opt)}
                    style={{
                      padding: '12px 8px',
                      borderRadius: 10,
                      border: sel ? `2px solid ${BRAND_BLUE}` : '2px solid transparent',
                      backgroundColor: sel ? BRAND_BLUE : 'white',
                      color: sel ? 'white' : BRAND_NAVY,
                      fontSize: 14, fontWeight: 600,
                      fontFamily: 'Inter, sans-serif',
                      cursor: 'pointer',
                      textTransform: 'capitalize'
                    }}
                  >
                    {opt}
                  </button>
                )
              })}
            </div>
          </div>

          {extractedData && (extractedData.firstName || extractedData.lastName || extractedData.streetAddress) && (
            <div style={{
              backgroundColor: 'rgba(255,255,255,0.06)',
              borderRadius: 12, padding: '12px 14px'
            }}>
              <div style={{
                fontSize: 11, color: '#9ca3af', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: 0.5,
                marginBottom: 10, fontFamily: 'Inter, sans-serif'
              }}>{t('cardScanner.alsoDetected', 'Also detected — tap to edit')}</div>
              <div style={{ display: 'grid', gap: 10 }}>
                {[
                  { key: 'firstName',     value: editedFirstName,     setter: setEditedFirstName,     auto: 'given-name' },
                  { key: 'lastName',      value: editedLastName,      setter: setEditedLastName,      auto: 'family-name' },
                  { key: 'streetAddress', value: editedStreetAddress, setter: setEditedStreetAddress, auto: 'street-address' },
                  { key: 'city',          value: editedCity,          setter: setEditedCity,          auto: 'address-level2' },
                  { key: 'state',         value: editedState,         setter: setEditedState,         auto: 'address-level1' },
                  { key: 'zipCode',       value: editedZipCode,       setter: setEditedZipCode,       auto: 'postal-code' },
                ].filter(f => extractedData[f.key]).map((f) => (
                  <div key={f.key} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    fontFamily: 'Inter, sans-serif',
                  }}>
                    <span style={{
                      flex: '0 0 90px', fontSize: 12, color: '#9ca3af',
                    }}>{formatFieldName(f.key)}</span>
                    <input
                      value={f.value}
                      onChange={(e) => f.setter(e.target.value)}
                      autoComplete={f.auto}
                      style={{
                        flex: 1, minWidth: 0,
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 8,
                        padding: '8px 10px',
                        fontSize: 14, fontWeight: 500,
                        color: 'white',
                        fontFamily: 'Inter, sans-serif',
                        outline: 'none',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = BRAND_BLUE }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{
          padding: '16px',
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
          backgroundColor: 'rgba(0,0,0,0.85)',
          display: 'flex', gap: 12
        }}>
          <button
            onClick={handleRetake}
            style={{
              flex: 1, padding: 14,
              backgroundColor: '#374151', color: 'white',
              border: 'none', borderRadius: 12,
              fontSize: 16, fontWeight: 500,
              fontFamily: 'Inter, sans-serif',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 8
            }}
          >
            <RotateCcw size={18} />
            {t('cardScanner.retake', 'Retake')}
          </button>
          <button
            onClick={handleSubmitConfirmed}
            disabled={!dobValid || !sexValid}
            style={{
              flex: 1.4, padding: 14,
              backgroundColor: (dobValid && sexValid) ? BRAND_NAVY : '#4b5563',
              color: 'white', border: 'none', borderRadius: 12,
              fontSize: 16, fontWeight: 600,
              fontFamily: 'Inter, sans-serif',
              cursor: (dobValid && sexValid) ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 8,
              opacity: (dobValid && sexValid) ? 1 : 0.7
            }}
          >
            <Check size={18} />
            {t('deliver.confirm', 'Confirm')}
          </button>
        </div>
      </div>
    ), document.body)
  }

  // ===== Render: Camera + Preview (default flow) =====
  return createPortal((
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: '#000',
      zIndex: 99999,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        padding: '16px',
        paddingTop: 'max(16px, env(safe-area-inset-top))',
        backgroundColor: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <button
          onClick={onCancel}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            padding: '8px'
          }}
        >
          <X size={24} />
        </button>
        <div style={{ color: 'white', textAlign: 'center' }}>
          <div style={{ fontWeight: '600', fontFamily: 'Inter, sans-serif' }}>{cardType} Card</div>
          <div style={{ fontSize: '12px', color: '#9ca3af' }}>{side} side</div>
        </div>
        <div style={{ width: '40px' }} />
      </div>

      <div ref={stageRef} style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {mode === 'camera' && (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                position: 'absolute',
                top: 0, left: 0,
                width: '100%', height: '100%',
                objectFit: 'cover'
              }}
            />

            {/* v213.2 (C-A-190): Larger card frame — was 85%/340px, now 92%/420px */}
            <div style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}>
              <div ref={frameRef} style={{
                position: 'relative',
                width: '92%',
                maxWidth: '420px',
                aspectRatio: CARD_ASPECT_RATIO.toString(),
                border: `3px solid ${BRAND_BLUE}`,
                borderRadius: '14px',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)'
              }}>
                <div style={{ position: 'absolute', top: -3, left: -3, width: 28, height: 28, borderTop: '4px solid #fff', borderLeft: '4px solid #fff', borderRadius: '4px 0 0 0' }} />
                <div style={{ position: 'absolute', top: -3, right: -3, width: 28, height: 28, borderTop: '4px solid #fff', borderRight: '4px solid #fff', borderRadius: '0 4px 0 0' }} />
                <div style={{ position: 'absolute', bottom: -3, left: -3, width: 28, height: 28, borderBottom: '4px solid #fff', borderLeft: '4px solid #fff', borderRadius: '0 0 0 4px' }} />
                <div style={{ position: 'absolute', bottom: -3, right: -3, width: 28, height: 28, borderBottom: '4px solid #fff', borderRight: '4px solid #fff', borderRadius: '0 0 4px 0' }} />
              </div>
            </div>

            <div style={{
              position: 'absolute',
              top: '6%', left: 0, right: 0,
              textAlign: 'center', padding: '0 16px'
            }}>
              <span style={{
                backgroundColor: 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '10px 18px',
                borderRadius: '999px',
                fontSize: '14px',
                fontWeight: 500,
                fontFamily: 'Inter, sans-serif',
                display: 'inline-block',
                maxWidth: '100%'
              }}>
                Fill the frame · keep card flat · good lighting
              </span>
            </div>

            {cameraError && (
              <div style={{
                position: 'absolute',
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(220, 38, 38, 0.9)',
                color: 'white',
                padding: '16px',
                borderRadius: '12px',
                textAlign: 'center',
                fontFamily: 'Inter, sans-serif',
                maxWidth: '88%'
              }}>
                <div>{cameraError}</div>
                {/* v240.11.0: camera unavailable — upload is the way out, not just X. */}
                {onUpload && (
                  <button
                    onClick={handleUploadPick}
                    style={{
                      marginTop: '12px',
                      padding: '10px 18px',
                      background: 'white',
                      color: BRAND_NAVY,
                      border: 'none',
                      borderRadius: '999px',
                      fontSize: '14px',
                      fontWeight: 600,
                      fontFamily: 'Inter, sans-serif',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Upload size={16} />
                    {t('cardScanner.uploadAPhoto', 'Upload a photo')}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {mode === 'preview' && capturedImage && (
          <div style={{
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            padding: '20px',
            overflowY: 'auto'
          }}>
            <div style={{ width: '100%', maxWidth: '420px' }}>
              <img
                src={capturedImage}
                alt={`${cardType} ${side}`}
                style={{ width: '100%', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }}
              />

              {extracting && (
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <span style={{
                    backgroundColor: BRAND_BLUE,
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '999px',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <OrellaSpinner size={16} tone="inherit" />
                    {t('cardScanner.readingCard', 'Reading card...')}
                  </span>
                </div>
              )}

              {!extracting && extractedData && Object.keys(extractedData).length > 0 && (
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <span style={{
                    backgroundColor: BRAND_GREEN, color: 'white',
                    padding: '6px 16px', borderRadius: '999px',
                    fontSize: '14px', fontWeight: 500, fontFamily: 'Inter, sans-serif',
                    display: 'inline-flex', alignItems: 'center', gap: 6
                  }}>
                    <Sparkles size={15} /> {t('cardScanner.cardRead', 'Card read')}
                  </span>
                  {needsConfirm && (
                    <div style={{
                      marginTop: 8, fontSize: 12, color: BRAND_LIGHT_BLUE,
                      fontFamily: 'Inter, sans-serif'
                    }}>
                      You'll confirm a couple of fields next.
                    </div>
                  )}
                </div>
              )}

              {!extracting && !extractedData && ocrSide && (
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <span style={{
                    backgroundColor: BRAND_GREEN, color: 'white',
                    padding: '6px 16px', borderRadius: '999px',
                    fontSize: '14px', fontWeight: 500,
                    fontFamily: 'Inter, sans-serif'
                  }}>
                    ✓ Card captured
                  </span>
                </div>
              )}

              {!ocrSide && (
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <span style={{
                    backgroundColor: BRAND_GREEN, color: 'white',
                    padding: '6px 16px', borderRadius: '999px',
                    fontSize: '14px', fontWeight: 500,
                    fontFamily: 'Inter, sans-serif'
                  }}>
                    ✓ Card captured
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {processing && (
          <div style={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ color: 'white', textAlign: 'center', fontSize: '18px', fontFamily: 'Inter, sans-serif' }}>
              {t('diagnose.processing', 'Processing...')}
            </div>
          </div>
        )}
      </div>

      <div style={{
        padding: '16px',
        paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
        backgroundColor: 'rgba(0,0,0,0.85)'
      }}>
        {mode === 'camera' && (
          <>
            <button
              onClick={capturePhoto}
              disabled={processing || !cameraReady}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: (!cameraReady || processing) ? '#4b5563' : BRAND_NAVY,
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '17px',
                fontWeight: '600',
                fontFamily: 'Inter, sans-serif',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Camera size={22} />
              {processing ? 'Processing...' : cameraReady ? 'Capture Card' : 'Starting camera...'}
            </button>

            {/* v240.11.0: upload-from-device option so the patient never has to X
                out of the camera just to pick a file. Selecting one hands off to
                the parent's upload pipeline and closes the scanner. */}
            {onUpload && (
              <button
                onClick={handleUploadPick}
                disabled={processing}
                style={{
                  width: '100%',
                  marginTop: '10px',
                  padding: '13px',
                  background: 'transparent',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '500',
                  fontFamily: 'Inter, sans-serif',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: processing ? 0.5 : 1
                }}
              >
                <Upload size={18} />
                {t('cardScanner.uploadAPhotoInstead', 'Upload a photo instead')}
              </button>
            )}
          </>
        )}

        {mode === 'preview' && (
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleRetake}
              disabled={extracting}
              style={{
                flex: 1,
                padding: '14px',
                backgroundColor: '#374151',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '500',
                fontFamily: 'Inter, sans-serif',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: extracting ? 0.5 : 1
              }}
            >
              <RotateCcw size={18} />
              {t('cardScanner.retake', 'Retake')}
            </button>
            <button
              onClick={handleConfirmPhoto}
              disabled={extracting}
              style={{
                flex: 1.4,
                padding: '14px',
                backgroundColor: extracting ? '#4b5563' : BRAND_NAVY,
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                fontFamily: 'Inter, sans-serif',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {extracting ? (
                <>
                  <OrellaSpinner size={18} tone="inherit" />
                  {t('cardScanner.reading', 'Reading...')}
                </>
              ) : (
                <>
                  <Check size={18} />
                  {needsConfirm ? 'Use Photo →' : 'Use Photo'}
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* v240.11.0: hidden input backing the in-scanner upload buttons */}
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*"
        onChange={handleUploadFile}
        style={{ display: 'none' }}
      />
    </div>
  ), document.body)
}
